<template>
  <div id="board" class="board initiative-list-view" :key="'list-view-' + viewId">
    <div class="loader-section w-100" v-if="!isInitialViewLoaded && isFetchingGroups">
      <initiative-skeleton class="initiative-skeleton" />
      <initiative-skeleton class="initiative-skeleton" />
      <initiative-skeleton class="initiative-skeleton" />
      <initiative-skeleton class="initiative-skeleton" />
    </div>
    <component
      :is="isFlatList ? 'infinite-scroll' : 'div'"
      class="board-content initiatives-view-scroll-container"
      @bottom="onScrollToBottom"
      :has-more-items="!!nextUrl"
      v-else
    >
      <div class="d-inline-block min-w-100" ref="listContainer" :key="listViewKey">
        <template v-for="(group, index) in processedGroups">
          <initiative-list-group
            :data-id="groupInfo[index].name"
            :data-group-index="index"
            :group="group"
            :key="index"
            :group-index="index"
            :sort-meta="sortMeta"
            :is-sortable="isFlatList"
            :group-info="groupInfo[index]"
            :ref="getGroupRefName(index)"
            :team-id="teamId"
            :org-id="orgId"
            :fields-to-display="fieldsToDisplay"
            :hide-gripper="hideGripper"
            :show-parent="showParent"
            :isExpanded="processedGroups.length === 1"
            :project-id="projectId"
            :auto-save-column-settings="autoSaveColumnSettings"
            @sort="$emit('sort', $event)"
            @reset="$emit('reset-sort', $event)"
            @filter="filterInitiatives"
            @add="$emit('add', $event)"
            @save="$emit('save', $event)"
            @update="$emit('update', $event)"
            @remove="$emit('remove', $event)"
            @add-column="$emit('add-column', $event)"
            @delete-section="$emit('reload-data')"
          />
        </template>
      </div>
      <add-section
        @added="$emit('section-added', $event)"
        :project-id="projectId"
        :org-id="orgId"
        v-if="isGroupedBySection"
      />
    </component>
  </div>
</template>

<script>
import { trackEvent } from '@/analytics'
import AddSection from '@/components/board/AddSection.vue'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import { selectItem } from '@/services/keymapService/keymapService'
import getParentVueComponent from '@/utils/getParentVueComponent'
import isLocalId from '@/utils/initiative/isLocalId'
import * as R from 'ramda'
import InitiativeListGroup from '@/components/board/InitiativeListGroup'
import InfiniteScroll from '@/components/InfiniteScroll.vue'
import InitiativeSkeleton from '@/components/skeletons/InitiativeSkeleton'
import {
  GROUP_BY_NONE_ID,
  GROUP_BY_SECTION_ID,
  INITIATIVE_FIELDS,
  INITIATIVE_ROW
} from '@/constants/initiative'
import InitiativesKeyboardNavMixins from '@/mixins/InitiativesKeyboardNavMixins'
import * as keymapService from '@/services/keymapService/keymapService'
import filterInitiatives from '@/utils/filterItems'
import selectFirstItem from '@/utils/selectFirstItem'
import { mapActions } from 'vuex'

/**
 * This components shows initiatives in a list view
 * @emits
 * - add: triggered when we need to add a new initiatives
 * - save: triggered when newly created initiative is saved
 * - remove: triggered when we need to remove an initiative. e.g on escape remove initiative that is not yet saved
 * - update: triggered when an initiative is updated
 */
export default {
  name: 'InitiativesListView',
  props: [
    'groups',
    'isFetchingGroups',
    'viewId',
    'groupInfo',
    'groupBy',
    'teamId',
    'orgId',
    'nextUrl',
    'sortMeta',
    'fieldsToDisplay',
    'isInitialViewLoaded',
    'hideGripper',
    'showParent',
    'projectId',
    'autoSaveColumnSettings'
  ],
  mixins: [InitiativesKeyboardNavMixins, DragAndDropMixin],
  components: {
    AddSection,
    InitiativeSkeleton,
    InitiativeListGroup,
    InfiniteScroll
  },
  data() {
    return {
      statusKey: INITIATIVE_FIELDS.STATUS.id,
      filteredBy: {},
      listViewKey: 1
    }
  },
  computed: {
    /**
     *This processes the groups. First it applies filter, then sorts them.
     */
    processedGroups() {
      let groups = this.groups
      if (this.filteredBy) {
        groups = groups.map(group =>
          filterInitiatives({
            filteredBy: this.filteredBy,
            items: group
          })
        )
      }
      if (this.sortedBy) {
        // implement sorting
      }
      return groups
    },
    isFlatList() {
      return this.groupBy === GROUP_BY_NONE_ID
    },
    isGroupedBySection() {
      return this.groupBy === GROUP_BY_SECTION_ID
    }
  },
  methods: {
    ...mapActions(['changeRank']),
    getGroupNameByIndex(index) {
      return this.groupInfo[index]?.name
    },
    filterInitiatives({ filterParam, filterValue, selected }) {
      let appliedFilters = this.filteredBy[filterParam] || {}
      appliedFilters = { ...appliedFilters, [filterValue]: selected }
      this.filteredBy = { ...this.filteredBy, [filterParam]: appliedFilters }
      this.initiatives = filterInitiatives({
        filteredBy: this.filteredBy,
        items: this.initiativesCopy
      })
    },
    onScrollToBottom() {
      this.$emit('fetch-next')
    },
    getSelectedGroup(el) {
      if (el.nodeName === 'BODY') return null
      else if (!R.isNil(el.dataset.groupIndex)) return +el.dataset.groupIndex
      else return this.getSelectedGroup(el.parentElement)
    },
    addInitiative() {
      const selectedItem = keymapService.selectedListItem(this.$el)
      const selectedGroupIndex = this.getSelectedGroup(selectedItem)
      if (selectedGroupIndex === null || isNaN(selectedGroupIndex)) return
      if (this.isInitiativeSelected()) {
        this.$emit('add', selectedGroupIndex)
      } else if (this.isTaskSelected()) {
        const initiative = getParentVueComponent(selectedItem, INITIATIVE_ROW)
        initiative.$refs.tasks.create()
      }
    },
    getGroupRefName(index) {
      return 'group-' + this.getGroupNameByIndex(index)
    },
    enableListGroupDragAndDrop() {
      this.enableDragAndDrop({
        containers: [this.$refs.listContainer],
        onDrop: this.onDrop,
        onDragEnd: this.onDragEnd,
        handleName: 'section-gripper'
      })
    },
    async onDrop({ afterId, beforeId, subjectId }) {
      try {
        await this.changeRank({
          orgId: this.orgId,
          type: 'section',
          subjectId,
          beforeId,
          afterId
        })
        trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.REARRANGE, EVENT_NAME.SECTION)
      } catch (e) {
        // Revert the drag. Re-rendering the list
        this.listViewKey++
        this.$nextTick(function() {
          // This should be inside nextTick because drag and drop should be re-enabled after list is rendered
          this.enableListGroupDragAndDrop()
        })
      }
    },
    getNewlyCreatedInitiative(newGroups, oldGroups) {
      const creationGroupIndex = oldGroups.findIndex(group =>
        isLocalId(group[group.length - 1]?.id)
      )
      const itemIndex = oldGroups[creationGroupIndex]?.length - 1
      const createdItemId = newGroups[creationGroupIndex]?.[itemIndex]?.id
      if (createdItemId && !isLocalId(createdItemId)) {
        return createdItemId
      }
      return null
    },
    selectInitiative(initiativeId) {
      selectItem(this.$el.querySelector(`[data-initiative-id="${initiativeId}"]`))
    }
  },
  mounted() {
    keymapService.activateCategory('initiativesListView', {
      'previous-item': this.withBulkSelectionHandler(this.selectPreviousItem),
      'next-item': this.withBulkSelectionHandler(this.selectNextItem),
      'view-item': this.withBulkSelectionHandler(this.viewItem),
      'expand-item': this.withBulkSelectionHandler(this.expandItem),
      'collapse-item': this.withBulkSelectionHandler(this.collapseItem),
      'toggle-expansion': this.withBulkSelectionHandler(() => this.toggleItem()),
      'edit-initiative': this.withBulkSelectionHandler(this.editInitiative),
      'done-undone-item': this.withBulkSelectionHandler(this.doneUndone),
      'cancel-editing': this.cancelInitiativeEditing,
      'add-initiative': this.addInitiative,
      comment: this.withBulkSelectionHandler(this.openActivityModal)
    })
    this.$store.dispatch('getDisplayInitiativeFields', {
      orgId: this.orgId,
      teamId: this.teamId,
      projectId: this.projectId
    })
    if (this.groups.length === 0) {
      this.enableListGroupDragAndDrop()
    }
  },
  beforeDestroy() {
    keymapService.deactivateCategory('initiativesListView')
  },
  watch: {
    groups(newValue, oldValue) {
      if (this.groups.length > 0) {
        const newlyCreatedInitiativeId = this.getNewlyCreatedInitiative(newValue, oldValue)
        this.$nextTick(() => {
          this.enableListGroupDragAndDrop()
          const selectedItem = keymapService.selectedListItem(this.$el)
          if (newlyCreatedInitiativeId) {
            this.selectInitiative(newlyCreatedInitiativeId)
          } else if (!selectedItem) {
            selectFirstItem(this.$el)
            this.focusTabPin()
          }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/components/_board.scss';
@import '~@/styles/_styleConfigurations.scss';

.loader-section {
  margin: 52px 64px;
}

.initiative-list-view {
  width: 100%;
  background: none;
  border: none;
  padding: 0;
}

.board-content {
  width: 100%;
  overflow-x: auto;
}

.initiative-skeleton {
  height: 64px;
}

@include media-breakpoint-up(md) {
  /deep/ .initiative-row:last-child .cell {
    border-bottom: 1px solid #edeff1;
  }
}

@include media-breakpoint-down(md) {
  .loader-section {
    margin: 4px 12px;
  }
}
</style>

<style lang="scss">
.gu-mirror.card {
  box-shadow: 0px 1px 2px rgba(66, 66, 66, 0.25), 0px 15px 20px #c1c1c1;
  transform: rotate(2.79deg);
}
</style>
